// https://www.w3schools.com/js/js_cookies.asp
import { BooleanOptional, IStringifyOptions, parse, stringify } from "qs";
import { localStorageFiltersKey } from "./constants/constants";

export function getCookie(cname: string) {
  var name = cname + "=";
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

// https://stackoverflow.com/a/61596084/1381550
export const inIFrame = window !== window.parent;

/**
 * Amends a URL that already may or may not have query parameters with
 * additional query parameters.
 * @todo, this is currently only being used for Scheduled Export, but we need settings passed also, so handling
 * as basic object there. Leaving here since I see this coming in handy.
 *
 * @param url {string}
 * @param query {?object}
 * @returns {*}
 */
export function amendUrlWithQuery(url: string, query = {}) {
  const [baseUrl, ...restParts] = url.split("?");
  const baseQuery = restParts ? parse(restParts.join("?")) : {};

  return (
    baseUrl +
    stringifyMinimal({ ...baseQuery, ...query }, { addQueryPrefix: true })
  );
}

const encodeMapping = {
  "#": "%23",
  "&": "%26",
  "+": "%2B",
};

let toReplaceCharactersRegExp;

/**
 * Like qs.stringify(), but only encodes as little as necessary, in order for
 * the resulting query string to be as human-readable as possible.
 */
export function stringifyMinimal(
  queryObject: any,
  additionalOptions?: IStringifyOptions<BooleanOptional>
) {
  return stringify(queryObject, {
    encoder: encodeURIMinimal,
    encodeValuesOnly: true,
    ...additionalOptions,
  });
}

/**
 * Deeply maps an object/array to change empty arrays into empty strings.
 *
 * This is for cases when using a form-encoded payload for Laravel,
 * because Laravel will be able to treat it as an empty array and act on
 * it. And, if you don't then do this then the empty array would be completely
 * omitted, and any endpoint that works PATCH-like would ignore the property.
 */
function normalizeEmptyArraysIntoEmptyStrings(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.length === 0
      ? ""
      : value.map(normalizeEmptyArraysIntoEmptyStrings);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        k,
        normalizeEmptyArraysIntoEmptyStrings(v),
      ])
    );
  }
  return value;
}

/**
 * Like qs.stringify(), but optimized for sending form requests to Laravel.
 */
export function stringifyForLaravel(
  queryObject: any,
  additionalOptions?: IStringifyOptions<BooleanOptional>
) {
  return stringify(normalizeEmptyArraysIntoEmptyStrings(queryObject), {
    serializeDate: (d) => d.toISOString(),
    encoder: (value, defaultEncoder, charset, type) => {
      // This is how Laravel expects booleans.
      if (type === "value" && value === true) return "1";
      if (type === "value" && value === false) return "0";

      return defaultEncoder(value, defaultEncoder, charset);
    },
    ...additionalOptions,
  });
}

/**
 * Like encodeURIComponent, but conservative on which characters it encodes.
 */
export function encodeURIMinimal(uriComponent: any) {
  if (typeof uriComponent !== "string") {
    return uriComponent;
  }
  if (!toReplaceCharactersRegExp) {
    toReplaceCharactersRegExp = new RegExp(
      Object.keys(encodeMapping)
        .map((v) => `\\${v}`)
        .join("|"),
      "g"
    );
  }
  return uriComponent?.replace(
    toReplaceCharactersRegExp,
    escapeURIMinimalReplacement
  );
}

function escapeURIMinimalReplacement(character) {
  return encodeMapping[character] ?? character;
}

export const originalQueryParsed = Object.freeze(
  parse(window.initialWindowLocationSearch, {
    ignoreQueryPrefix: true,
  })
);

/**
 * @deprecated Should be removed in the future once most localStorage filters
 *  have gotten saved to the API.
 * @returns {any|{}}
 */
export const getFiltersFromLocalStorage = () => {
  return JSON.parse(localStorage.getItem(localStorageFiltersKey)!) ?? {};
};
